import { useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import API from '@utils/api';
import PageContext from '@contexts/PageContext';
import { getLanguageId } from '@helpers/language';
import { CategoryGroup, ProductGroup, ProductPage } from '@containers';

const NOT_FOUND_RESULT = {
  notFound: true,
  redirect: {
    permanent: true,
    destination: '/404'
  }
};

const Page = ({ data }) => {
  const { locale } = useRouter();
  const { ProductType, QuickFeatures } = data;
  const langId = getLanguageId(locale);

  const firstQuickFeatureText = useMemo(() => {
    const quickFeatures = QuickFeatures?.filter((qf) => qf.FeatureText.LanguageId === langId);

    return quickFeatures?.length ? quickFeatures[0].FeatureText : null;
  }, [QuickFeatures]);

  let PageContainer;

  switch (ProductType) {
    case 6:
    case 11:
      PageContainer = ProductPage;
      break;
    case 5:
    case 10:
      PageContainer = ProductGroup;
      break;
    default:
      PageContainer = CategoryGroup;
      break;
  }

  return (
    <PageContext.Provider value={data}>
      <Head>
        {firstQuickFeatureText && (
          <meta
            name="addsearch-custom-field"
            data-type="text"
            content={`feature=${firstQuickFeatureText?.FeatureTitle} ${firstQuickFeatureText?.FeatureText}`}
            key="feature"
          />
        )}
      </Head>
      <PageContainer />
    </PageContext.Provider>
  );
};

export const getServerSideProps = async ({ params, locale }) => {
  const { code } = params;
  let id;

  if (!isNaN(code)) {
    id = code;
  }

  try {
    var data = await API.post('/api/products', {
      ...(id ? { id } : { code }),
      locale
    });

    if (!data) {
      return NOT_FOUND_RESULT;
    }

    if (data.error) {
      console.error(`Error fetching product Code/ID: ${code ?? id}. Message: ${data.error.message ?? 'N/A'}`);
      throw data.error;
    }

    const checkTheme = data?.DisplayOptions?.find((x) => x.Name == 'Header')?.DisplayOptions?.find(
      (item) => item.DisplayOptionKey == 'DarkMode'
    )?.Enabled;

    data.DarkMode = checkTheme ? checkTheme : null;
  } catch (error) {
    console.error(`Error fetching product Code/ID: ${code ?? id}. Message: ${error.message}`);
    throw error;
  }

  return {
    props: {
      data,
      ...(await serverSideTranslations(locale, ['common', 'footer']))
    }
  };
};

export default Page;
